import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

// Shade chart hotspots — fractions of the banner image (left arm x:18-54, right arm x:58-100)
const SHADE_HOTSPOTS = [
    { key: "9", label: "#9 Ruby", y: 10.4, left: true },
    { key: "25", label: "#25 Leading Lady", y: 15.9, left: true },
    { key: "17", label: "#17 Lovely Beauty", y: 20.9, left: true },
    { key: "16", label: "#16 Jacinth", y: 25.9, left: true },
    { key: "2", label: "#2 Chocolate Beauty", y: 32.9, left: true },
    { key: "26", label: "#26 Boss up", y: 39.4, left: true },
    { key: "21", label: "#21 Lavish", y: 44.4, left: true },
    { key: "3", label: "#3 Nude Beauty", y: 49.9, left: true },
    { key: "19", label: "#19 Lip Goals", y: 55.4, left: true },
    { key: "11", label: "#11 Skin Beauty", y: 60.9, left: true },
    { key: "24", label: "#24 Graceful", y: 65.9, left: true },
    { key: "23", label: "#23 Bloodshot", y: 70.9, left: true },
    { key: "14", label: "#14 Orange Beauty", y: 76.4, left: true },
    { key: "22", label: "#22 Over You", y: 81.4, left: true },
    { key: "10", label: "#10 Crush", y: 84.4, left: true },
    { key: "7", label: "#7 Sage", y: 88.4, left: true },
    { key: "1", label: "#1 Ross Beauty", y: 19.4, left: false },
    { key: "4", label: "#4 Magenta", y: 23.4, left: false },
    { key: "13", label: "#13 Peach Beauty", y: 27.4, left: false },
    { key: "15", label: "#15 Rosa Beauty", y: 31.9, left: false },
    { key: "20", label: "#20 Pretty Woman", y: 36.9, left: false },
    { key: "29", label: "#29 Gorgeous", y: 41.4, left: false },
    { key: "6", label: "#6 Beige Beauty", y: 46.4, left: false },
    { key: "28", label: "#28 Bade Alert", y: 51.4, left: false },
    { key: "27", label: "#27 Out Loud", y: 56.4, left: false },
    { key: "8", label: "#8 Mauve Beauty", y: 61.4, left: false },
    { key: "12", label: "#12 Plum Beauty", y: 66.4, left: false },
    { key: "18", label: "#18 Cherry Beauty", y: 71.9, left: false },
    { key: "5", label: "#5 Sultry", y: 78.4, left: false },
];

const findShadeProduct = (products, key) => {
    const cosmetics = products.filter((p) => (p.category || "").toLowerCase().includes("cosmetic"));
    const re = new RegExp(`#\\s*0?${key}(?!\\d)`, "i");
    return cosmetics.find((p) => re.test(p.name));
};

export default function ShopPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const [category, setCategory] = useState(searchParams.get("category") || "All");
    const [query, setQuery] = useState(searchParams.get("search") || "");
    const searchRef = useRef(null);

    useEffect(() => {
        setCategory(searchParams.get("category") || "All");
        setQuery(searchParams.get("search") || "");
        if (searchParams.get("focus")) {
            setTimeout(() => searchRef.current?.focus(), 300);
        }
    }, [searchParams]);

    useEffect(() => {
        api.get("/products")
            .then((res) => setProducts(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const categories = ["All", ...new Set(products.map((p) => p.category))];
    const q = query.trim().toLowerCase();
    const searched = q
        ? products.filter((p) => [p.name, p.description, p.category].join(" ").toLowerCase().includes(q))
        : products;
    const filtered = category === "All" ? searched : searched.filter((p) => p.category === category);
    const shadeNumber = (name) => {
        const m = (name || "").match(/#\s*(\d+)|\b(\d+)\b/);
        return m ? parseInt(m[1] || m[2], 10) : null;
    };
    const displayed = [...filtered].sort((a, b) => {
        const na = shadeNumber(a.name);
        const nb = shadeNumber(b.name);
        if (na != null && nb != null) return na - nb;
        return 0;
    });

    return (
        <main data-testid="shop-page" className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay" data-testid="shop-count">
                    {filtered.length} piece{filtered.length === 1 ? "" : "s"} — each one of one
                </p>
                <h1 className="mt-4 font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl">
                    The <span className="italic text-espresso">Collection</span>
                </h1>
            </motion.div>
            {category === "Cosmetics" && (
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                    className="mx-auto mt-12 max-w-2xl text-center"
                    data-testid="cosmetics-banner"
                >
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">Every shade, swatched</p>
                    <p className="mt-3 font-serif text-2xl font-light italic text-gold md:text-3xl">The Kicha shade chart</p>
                    <div className="relative mx-auto mt-8 w-fit">
                        <div className="absolute -left-3 -top-3 h-full w-full border border-espresso" aria-hidden="true" />
                        <div className="relative overflow-hidden bg-white p-2">
                            <div className="relative">
                                <img
                                    src="/cosmetics-banner.jpg"
                                    alt="Kichazone lipstick shade chart — every shade swatched"
                                    className="block max-h-[75vh] w-auto object-contain"
                                    data-testid="cosmetics-banner-image"
                                />
                                {SHADE_HOTSPOTS.map((s) => {
                                    const product = findShadeProduct(products, s.key);
                                    if (!product) return null;
                                    return (
                                        <Link
                                            key={s.key}
                                            to={`/product/${product.id}`}
                                            data-testid={`shade-link-${s.key}`}
                                            aria-label={`Shop ${s.label}`}
                                            className="group absolute block"
                                            style={{
                                                left: s.left ? "18%" : "58%",
                                                top: `${s.y}%`,
                                                width: s.left ? "36%" : "42%",
                                                height: "3.6%",
                                            }}
                                        >
                                            <span className="absolute inset-0 border border-transparent transition-all duration-200 group-hover:border-gold group-hover:bg-gold/10" />
                                            <span className={`absolute top-1/2 hidden -translate-y-1/2 whitespace-nowrap bg-espresso px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-sand opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 md:block ${s.left ? "-left-2 -translate-x-full" : "-right-2 translate-x-full"}`}>
                                                {s.label} →
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Tap a shade to shop it — or browse below</p>
                </motion.div>
            )}
            <div className="mt-10 flex items-center border border-line bg-white px-4 py-3 transition-colors focus-within:border-espresso" data-testid="shop-search-bar">
                <Search size={16} className="shrink-0 text-clay" />
                <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="shop-search-input"
                    placeholder="Search purses, jewelry, blankets…"
                    className="ml-3 w-full bg-transparent text-sm outline-none placeholder:text-clay/60"
                />
                {query && (
                    <button onClick={() => setQuery("")} data-testid="shop-search-clear" className="text-clay transition-colors hover:text-crimson">
                        <X size={14} />
                    </button>
                )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="shop-filters">
                {categories.map((c) => (
                    <button
                        key={c}
                        onClick={() => setCategory(c)}
                        data-testid={`filter-${c.toLowerCase()}`}
                        className={`border px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                            category === c ? "border-espresso bg-espresso text-sand" : "border-line hover:border-espresso"
                        }`}
                    >
                        {c}
                    </button>
                ))}
            </div>
            {loading ? (
                <p className="mt-24 font-serif text-2xl italic text-clay" data-testid="shop-loading">Unwinding the yarn…</p>
            ) : filtered.length === 0 ? (
                <p className="mt-24 font-serif text-2xl italic text-clay" data-testid="shop-empty">
                    {query ? `Nothing found for “${query}” — try another word.` : "Nothing here yet — new pieces drop every week."}
                </p>
            ) : (
                <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
                    {displayed.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            )}
        </main>
    );
}
